import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getActiveTripSummary,
  getTripLastKnownDriverLocation,
  getTripTrackingLatestLocation,
  getTripTrackingState,
} from '../services/api/bookings';
import { readCoordinates, readHeading, subscribeToTrip } from '../services/tripSocket';
import type {
  ActiveInstantTripSummary,
  TripTrackingLatestLocation,
  TripTrackingSnapshot,
} from '../types/booking';

/**
 * Live state for one trip.
 *
 * The socket is the fast path; polling is the floor. If the socket never
 * connects — blocked network, proxy that drops upgrades, backend restart — the
 * customer still sees their delivery advance, just less often. A tracking page
 * that goes blank because a WebSocket failed would be worse than no tracking
 * page, because the customer cannot tell the difference between "no update" and
 * "nothing is happening".
 *
 * Polling summary keeps status alive. Polling tracking location keeps the map
 * alive, matching the mobile app's fallback path when the socket is delayed or
 * blocked by the network.
 */

const SUMMARY_POLL_INTERVAL_MS = 20000;
const LOCATION_POLL_INTERVAL_MS = 8000;
const LOCATION_STALE_AFTER_MS = 45000;

export interface TripTrackingState {
  trip: ActiveInstantTripSummary | null;
  driverPosition: { lat: number; lng: number } | null;
  /** Degrees, when the feed supplies one. Null keeps the marker undirected. */
  driverHeading: number | null;
  /** ETA from live tracking frames, which is fresher than the active-trip summary. */
  liveEtaSeconds: number | null;
  routePolyline: string | null;
  connected: boolean;
  /** True until the first successful load, socket or poll. */
  loading: boolean;
  /** Set when the trip cannot be found at all. */
  missing: boolean;
}

export function useTripTracking(tripId: string | undefined): TripTrackingState {
  const [trip, setTrip] = useState<ActiveInstantTripSummary | null>(null);
  const [driverPosition, setDriverPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [driverHeading, setDriverHeading] = useState<number | null>(null);
  const [liveEtaSeconds, setLiveEtaSeconds] = useState<number | null>(null);
  const [routePolyline, setRoutePolyline] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  /* Read inside the interval without making it a dependency, so a connection
     change does not tear down and rebuild the timer. */
  const connectedRef = useRef(connected);
  connectedRef.current = connected;
  const latestLocationAtMsRef = useRef(0);
  const latestSequenceRef = useRef<number | null>(null);
  const locationPollInFlightRef = useRef(false);

  const applyTrackingPayload = useCallback((payload: Record<string, unknown> | null | undefined) => {
    if (!payload) return;

    const asNumber = (value: unknown): number | null => {
      const parsed = typeof value === 'string' ? Number(value) : value;
      return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
    };

    const position = readCoordinates(payload);
    if (position) {
      setDriverPosition(position);
      latestLocationAtMsRef.current = Date.now();
    }

    const heading = readHeading(payload);
    if (heading != null) setDriverHeading(heading);

    const nextPolyline =
      typeof payload.routePolyline === 'string'
        ? payload.routePolyline
        : typeof payload.route_polyline === 'string'
          ? payload.route_polyline
          : null;
    if (nextPolyline) setRoutePolyline(nextPolyline);

    const phase = typeof payload.phase === 'string' ? payload.phase : null;
    const etaToPickup =
      asNumber(payload.etaToPickupSec) ?? asNumber(payload.eta_to_pickup_sec);
    const etaToDropoff =
      asNumber(payload.etaToDropoffSec) ?? asNumber(payload.eta_to_dropoff_sec);
    const nextEta =
      phase === 'DRIVER_HEADING_TO_DROPOFF' || phase === 'AWAITING_COMPLETION'
        ? etaToDropoff ?? etaToPickup
        : etaToPickup ?? etaToDropoff;

    if (nextEta != null) setLiveEtaSeconds(nextEta);
  }, []);

  const applyLatestLocation = useCallback(
    (snapshot: TripTrackingLatestLocation | null) => {
      if (!snapshot) return;

      const sequence =
        typeof snapshot.sequenceNumber === 'number' && Number.isFinite(snapshot.sequenceNumber)
          ? snapshot.sequenceNumber
          : null;
      if (
        sequence != null &&
        latestSequenceRef.current != null &&
        sequence <= latestSequenceRef.current
      ) {
        return;
      }

      if (sequence != null) latestSequenceRef.current = sequence;
      applyTrackingPayload(snapshot as Record<string, unknown>);
    },
    [applyTrackingPayload],
  );

  const applyTrackingState = useCallback(
    (snapshot: TripTrackingSnapshot | null) => {
      if (!snapshot) return;

      if (snapshot.routePolyline) setRoutePolyline(snapshot.routePolyline);

      const breadcrumb = Array.isArray(snapshot.breadcrumb) ? snapshot.breadcrumb : [];
      const last = breadcrumb.length ? breadcrumb[breadcrumb.length - 1] : null;
      if (!last) return;

      applyLatestLocation({
        tripId: snapshot.tripId,
        driverId: snapshot.driverId,
        latitude: last.latitude,
        longitude: last.longitude,
        at: last.at ?? snapshot.lastSeenAt ?? snapshot.updatedAt,
        phase: snapshot.phase,
        etaToPickupSec: snapshot.etaToPickupSec,
        etaToDropoffSec: snapshot.etaToDropoffSec,
        routePolyline: snapshot.routePolyline,
        routeRevision: snapshot.routeRevision,
        routeDeviation: snapshot.routeDeviation,
        routeAvailable: snapshot.routeAvailable,
        routeStatus: snapshot.routeStatus,
        locationTrusted: snapshot.locationTrusted,
        trackingMode: snapshot.trackingMode,
        expectedUpdateIntervalSec: snapshot.expectedUpdateIntervalSec,
        lastSeenAt: snapshot.lastSeenAt,
        updatedAt: snapshot.updatedAt,
      });
    },
    [applyLatestLocation],
  );

  const refresh = useCallback(async () => {
    if (!tripId) return;

    try {
      const summary = await getActiveTripSummary();
      const match = summary.activeTrips.find(
        (candidate) => candidate.tripId === tripId || candidate.bookingId === tripId,
      );

      setTrip(match ?? null);
      setMissing(!match);
    } catch {
      /* Leave the last good state on screen rather than blanking it — a failed
         refresh is not evidence that the trip ended. */
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  const refreshTrackingState = useCallback(async () => {
    if (!tripId) return;

    try {
      applyTrackingState(await getTripTrackingState(tripId));
    } catch {
      /* Tracking state is auxiliary; status polling and last-location can still
         keep the page useful. */
    }
  }, [applyTrackingState, tripId]);

  const refreshLatestLocation = useCallback(async () => {
    if (!tripId || locationPollInFlightRef.current) return;

    locationPollInFlightRef.current = true;
    try {
      applyLatestLocation(await getTripTrackingLatestLocation(tripId));
    } catch {
      /* Keep the last marker position. The connection copy already tells the
         customer live updates are degraded. */
    } finally {
      locationPollInFlightRef.current = false;
    }
  }, [applyLatestLocation, tripId]);

  useEffect(() => {
    setDriverPosition(null);
    setDriverHeading(null);
    setLiveEtaSeconds(null);
    setRoutePolyline(null);
    latestLocationAtMsRef.current = 0;
    latestSequenceRef.current = null;
  }, [tripId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!tripId) return undefined;

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const [tracking, lastKnown] = await Promise.all([
          getTripTrackingState(tripId),
          getTripLastKnownDriverLocation(tripId),
        ]);
        if (cancelled) return;
        applyTrackingState(tracking);
        applyLatestLocation(lastKnown);
      } catch {
        if (!cancelled) void refreshTrackingState();
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [applyLatestLocation, applyTrackingState, refreshTrackingState, tripId]);

  useEffect(() => {
    if (!tripId) return undefined;

    const dispose = subscribeToTrip(tripId, {
      onTripStatus: (payload) => {
        setTrip((previous) => (previous ? { ...previous, ...payload } : previous));
        setMissing(false);
        setLoading(false);
      },
      onDriverLocation: (payload) => {
        applyLatestLocation(payload as TripTrackingLatestLocation);
      },
      onConnectionChange: setConnected,
    });

    return dispose;
  }, [applyLatestLocation, tripId]);

  useEffect(() => {
    if (!tripId) return undefined;

    const timer = window.setInterval(() => {
      if (!connectedRef.current) void refresh();
    }, SUMMARY_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [tripId, refresh]);

  useEffect(() => {
    if (!tripId) return undefined;

    const pollIfNeeded = () => {
      const locationIsStale =
        !latestLocationAtMsRef.current ||
        Date.now() - latestLocationAtMsRef.current > LOCATION_STALE_AFTER_MS;
      if (!connectedRef.current || locationIsStale) void refreshLatestLocation();
    };

    pollIfNeeded();
    const timer = window.setInterval(pollIfNeeded, LOCATION_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [refreshLatestLocation, tripId]);

  return {
    trip,
    driverPosition,
    driverHeading,
    liveEtaSeconds,
    routePolyline,
    connected,
    loading,
    missing,
  };
}
